"use client";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

import {
  createAnalyticsClientLoader,
  createPageViewTracker,
  schedulePageView,
} from "@/src/analytics/deferredPageView";
import { isTelemetryAllowedInBrowser, subscribeToConsent } from "@/src/features/consent/browserConsent";

// PostHog is ~60 KB of brotli JS that no first paint needs. When the consent
// policy allows analytics (a stored choice, or the default outside the
// EEA, UK and Switzerland), load the analytics client after the page is idle (or on the first
// interaction / page hide); otherwise only once analytics is allowed. Routes that already import it (workspace, auth
// callback) resolve this import instantly.
const loadAnalyticsClient = createAnalyticsClientLoader(() => import("@/src/analytics/client"));

const analyticsConsent = {
  allowed: () => isTelemetryAllowedInBrowser("analytics"),
  subscribe: subscribeToConsent,
};

function whenIdle(callback) {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 1);
  return () => window.clearTimeout(id);
}

export function PHProvider({ children }) {
  const pathname = usePathname();
  const trackerRef = useRef(null);
  trackerRef.current ??= createPageViewTracker();

  useEffect(
    () =>
      schedulePageView({
        pathname,
        tracker: trackerRef.current,
        load: loadAnalyticsClient,
        whenIdle,
        consent: analyticsConsent,
        target: window,
      }),
    [pathname],
  );

  return children;
}
